/**
 * Build-vs-buy page content SOT (launch-readiness B3).
 *
 * The page is the READER's decision, argued from their chair: they have
 * decided their product needs an agent surface, and the open question is
 * whether their own team builds it or they buy it. Every section serves that
 * decision — the real scope of an in-house build, how the build year actually
 * goes, the published delivery-risk data, an honest "when building is right /
 * when buying is right" split, and a side-by-side of the two paths. It is NOT
 * a vendor comparison (that is `/vs/<rival>`) and it is not a defense of the
 * product category — the reader has already decided they want the capability.
 *
 * Source discipline (`.claude/rules` + playbook "never publish untested
 * claims"): every statistic traces to the verified entries in
 * `product/market-and-gtm/market-reality-check-2026-06.md` (marked [V]).
 * No competitor is named anywhere on this page — a buyer weighing build vs
 * buy is not comparison shopping yet, and pinning that framing is what keeps
 * this page from drifting back into a pricing-model scoreboard
 * (BuildVsBuy.test.tsx enforces it).
 *
 * Copy lives in the i18n catalogs under `pricing.bvb.*` — the prefix is
 * historical (the page began at /pricing/build-vs-buy) and is kept because it
 * is a catalog-group router (`catalog-groups.ts` chunks on `pricing.`) and a
 * test handle, never something a reader sees.
 */

export interface EvidenceStat {
  readonly id: string;
  /** Verbatim headline figure (kept identical across locales). */
  readonly figure: string;
  /** i18n key for what the figure means. */
  readonly claimKey: string;
  /** Verbatim source attribution (proper noun + year — not translated). */
  readonly source: string;
}

/**
 * Cited findings — all [V]-verified in market-reality-check-2026-06.md. These
 * are figures about IN-HOUSE initiatives — the delivery risk of the build
 * path — which is why they belong on this page and category punditry does not.
 */
export const EVIDENCE_STATS: readonly EvidenceStat[] = [
  {
    id: 'nanda',
    figure: '95%',
    claimKey: 'pricing.bvb.evidence.nanda',
    source: 'MIT NANDA, Aug 2025',
  },
  {
    id: 'sp-abandonment',
    figure: '17% → 42%',
    claimKey: 'pricing.bvb.evidence.sp',
    source: 'S&P Global, 2025',
  },
  {
    id: 'gartner-cancel',
    figure: '40%',
    claimKey: 'pricing.bvb.evidence.gartner',
    source: 'Gartner, Jun 2025',
  },
  {
    id: 'roi-window',
    figure: '57%',
    claimKey: 'pricing.bvb.evidence.g2',
    source: 'G2, 2025',
  },
] as const;

export interface BuildScopeItem {
  readonly id: string;
  readonly titleKey: string;
  readonly detailKey: string;
}

/**
 * What the reader's team would actually be building — the full surface, not
 * only the hazards. Order is the order a build actually encounters them: the
 * product surface first (chat + MCP), then the plumbing that makes it safe,
 * then the work that never ends.
 */
export const BUILD_SCOPE_ITEMS: readonly BuildScopeItem[] = [
  {
    id: 'surface',
    titleKey: 'pricing.bvb.scope.surface.title',
    detailKey: 'pricing.bvb.scope.surface.detail',
  },
  {
    id: 'discovery',
    titleKey: 'pricing.bvb.scope.discovery.title',
    detailKey: 'pricing.bvb.scope.discovery.detail',
  },
  {
    id: 'auth',
    titleKey: 'pricing.bvb.scope.auth.title',
    detailKey: 'pricing.bvb.scope.auth.detail',
  },
  {
    id: 'safety',
    titleKey: 'pricing.bvb.scope.safety.title',
    detailKey: 'pricing.bvb.scope.safety.detail',
  },
  {
    id: 'tenancy',
    titleKey: 'pricing.bvb.scope.tenancy.title',
    detailKey: 'pricing.bvb.scope.tenancy.detail',
  },
  {
    id: 'i18n',
    titleKey: 'pricing.bvb.scope.i18n.title',
    detailKey: 'pricing.bvb.scope.i18n.detail',
  },
  {
    id: 'evals',
    titleKey: 'pricing.bvb.scope.evals.title',
    detailKey: 'pricing.bvb.scope.evals.detail',
  },
  {
    id: 'maintenance',
    titleKey: 'pricing.bvb.scope.maintenance.title',
    detailKey: 'pricing.bvb.scope.maintenance.detail',
  },
] as const;

export interface TimelineStage {
  readonly id: 'weekend' | 'monthThree' | 'monthTwelve';
  readonly titleKey: string;
  readonly detailKey: string;
}

/**
 * How the build year actually goes — the demo curve every team that has tried
 * this recognizes. Three stages, not a project plan: the point is where the
 * time goes, not a schedule.
 */
export const TIMELINE_STAGES: readonly TimelineStage[] = [
  {
    id: 'weekend',
    titleKey: 'pricing.bvb.timeline.weekend.title',
    detailKey: 'pricing.bvb.timeline.weekend.detail',
  },
  {
    id: 'monthThree',
    titleKey: 'pricing.bvb.timeline.monthThree.title',
    detailKey: 'pricing.bvb.timeline.monthThree.detail',
  },
  {
    id: 'monthTwelve',
    titleKey: 'pricing.bvb.timeline.monthTwelve.title',
    detailKey: 'pricing.bvb.timeline.monthTwelve.detail',
  },
] as const;

export interface DecisionReason {
  readonly id: string;
  readonly titleKey: string;
  readonly detailKey: string;
}

/**
 * When building in-house is the RIGHT call. This section is load-bearing for
 * the whole page's credibility: a build-vs-buy page that cannot name the
 * build cases is an ad, and the reader knows it.
 */
export const BUILD_REASONS: readonly DecisionReason[] = [
  {
    id: 'coreProduct',
    titleKey: 'pricing.bvb.whenBuild.coreProduct.title',
    detailKey: 'pricing.bvb.whenBuild.coreProduct.detail',
  },
  {
    id: 'platformTeam',
    titleKey: 'pricing.bvb.whenBuild.platformTeam.title',
    detailKey: 'pricing.bvb.whenBuild.platformTeam.detail',
  },
  {
    id: 'constraints',
    titleKey: 'pricing.bvb.whenBuild.constraints.title',
    detailKey: 'pricing.bvb.whenBuild.constraints.detail',
  },
] as const;

/** When buying is the right call — the mirror of BUILD_REASONS, same shape. */
export const BUY_REASONS: readonly DecisionReason[] = [
  {
    id: 'feature',
    titleKey: 'pricing.bvb.whenBuy.feature.title',
    detailKey: 'pricing.bvb.whenBuy.feature.detail',
  },
  {
    id: 'speed',
    titleKey: 'pricing.bvb.whenBuy.speed.title',
    detailKey: 'pricing.bvb.whenBuy.speed.detail',
  },
  {
    id: 'review',
    titleKey: 'pricing.bvb.whenBuy.review.title',
    detailKey: 'pricing.bvb.whenBuy.review.detail',
  },
] as const;

export interface DecisionRow {
  readonly id: string;
  readonly dimensionKey: string;
  readonly buildKey: string;
  readonly buyKey: string;
}

/**
 * The two paths side by side, one dimension per row. The `control` row
 * concedes the build path's genuine win — total control — because a table
 * where one column wins every row is a table nobody trusts.
 */
export const DECISION_ROWS: readonly DecisionRow[] = [
  {
    id: 'time',
    dimensionKey: 'pricing.bvb.decision.time.dimension',
    buildKey: 'pricing.bvb.decision.time.build',
    buyKey: 'pricing.bvb.decision.time.buy',
  },
  {
    id: 'headcount',
    dimensionKey: 'pricing.bvb.decision.headcount.dimension',
    buildKey: 'pricing.bvb.decision.headcount.build',
    buyKey: 'pricing.bvb.decision.headcount.buy',
  },
  {
    id: 'upfront',
    dimensionKey: 'pricing.bvb.decision.upfront.dimension',
    buildKey: 'pricing.bvb.decision.upfront.build',
    buyKey: 'pricing.bvb.decision.upfront.buy',
  },
  {
    id: 'churn',
    dimensionKey: 'pricing.bvb.decision.churn.dimension',
    buildKey: 'pricing.bvb.decision.churn.build',
    buyKey: 'pricing.bvb.decision.churn.buy',
  },
  {
    id: 'securityReview',
    dimensionKey: 'pricing.bvb.decision.securityReview.dimension',
    buildKey: 'pricing.bvb.decision.securityReview.build',
    buyKey: 'pricing.bvb.decision.securityReview.buy',
  },
  {
    id: 'control',
    dimensionKey: 'pricing.bvb.decision.control.dimension',
    buildKey: 'pricing.bvb.decision.control.build',
    buyKey: 'pricing.bvb.decision.control.buy',
  },
] as const;

/**
 * Verbatim anchors for the cost math (playbook field anchors). Interpolated
 * into the copy so the figures have ONE home — `pricing-uc-copy.test.ts`
 * guards the Starter price here against the ADR-0149 ladder.
 */
export const IN_HOUSE_ANCHORS = {
  engineerCost: '$200K',
  starterPrice: '$299/mo',
  // No tilde: every locale's sentence already says "near" / "cerca de" /
  // "etwa" / "סביב", so "near ~1M" said approximately twice, in all six.
  buyThreshold: '1M',
} as const;
